import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Chip,
  Alert,
  Card,
  CardContent,
  CardActions,
  Grid,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  AccessTime,
  Euro,
  People,
} from "@mui/icons-material";
import { bookingService } from "../services/bookingService";

const defaultLessonType = {
  name: "",
  duration: 15,
  price: "",
  description: "",
  maxParticipants: 1,
  color: "#F44336",
  isOnline: false,
  isGroup: false,
};

const colorOptions = [
  { value: "#F44336", label: "Rosso" },
  { value: "#FF9800", label: "Arancione" },
  { value: "#FFEB3B", label: "Giallo" },
  { value: "#4CAF50", label: "Verde" },
  { value: "#2196F3", label: "Azzurro" },
  { value: "#3F51B5", label: "Indaco" },
  { value: "#9C27B0", label: "Viola" },
];

function LessonTypeManager() {
  const [lessonTypes, setLessonTypes] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalEdit, setModalEdit] = useState(false);
  const [modalLessonType, setModalLessonType] = useState(defaultLessonType);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    loadLessonTypes();
  }, []);

  const loadLessonTypes = async () => {
    try {
      setLoading(true);
      const types = await bookingService.getLessonTypes();
      setLessonTypes(types);
    } catch (error) {
      console.error("Error loading lesson types:", error);
      setError("Errore nel caricamento dei tipi di lezione");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setModalLessonType(defaultLessonType);
    setModalEdit(false);
    setModalOpen(true);
    setError("");
    setSuccess("");
  };

  const handleEdit = (lessonType) => {
    setModalLessonType({ ...lessonType });
    setModalEdit(true);
    setModalOpen(true);
    setError("");
    setSuccess("");
  };

  const handleClose = () => {
    setModalOpen(false);
    setModalLessonType(defaultLessonType);
    setError("");
    setSuccess("");
  };

  const handleInputChange = (field, value) => {
    setModalLessonType((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const validateForm = () => {
    if (!modalLessonType.name.trim()) {
      setError("Il nome della lezione è obbligatorio");
      return false;
    }
    if (modalLessonType.duration <= 0) {
      setError("La durata deve essere maggiore di 0");
      return false;
    }
    if (modalLessonType.maxParticipants < 0) {
      setError("Il numero massimo di partecipanti non può essere negativo");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);
      setError("");

      if (modalEdit) {
        await bookingService.updateLessonType(modalLessonType.id, modalLessonType);
        setSuccess("Tipo di lezione aggiornato con successo!");
      } else {
        await bookingService.createLessonType(modalLessonType);
        setSuccess("Tipo di lezione creato con successo!");
      }

      await loadLessonTypes();
      handleClose();
    } catch (error) {
      console.error("Error saving lesson type:", error);
      setError("Errore nel salvataggio del tipo di lezione");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      setError("");
      setSuccess("");

      // Check if there are weekly slots using this lesson type
      const associatedSlots = await bookingService.getWeeklySlotsByLessonType(id);

      let confirmMessage = "Sei sicuro di voler eliminare questo tipo di lezione?";
      if (associatedSlots.length > 0) {
        confirmMessage = `Questo tipo di lezione è utilizzato in ${associatedSlots.length} slot del palinsesto.\n\nEliminando il tipo di lezione, verranno rimossi anche tutti gli slot associati.\n\nVuoi continuare?`;
      }

      if (!window.confirm(confirmMessage)) {
        setLoading(false);
        return;
      }

      // Delete associated slots first
      if (associatedSlots.length > 0) {
        await bookingService.deleteWeeklySlotsByLessonType(id);
      }

      // Then delete the lesson type
      await bookingService.deleteLessonType(id);
      await loadLessonTypes();

      const successMessage = associatedSlots.length > 0
        ? `Tipo di lezione e ${associatedSlots.length} slot del palinsesto eliminati con successo!`
        : "Tipo di lezione eliminato con successo!";
      setSuccess(successMessage);
    } catch (error) {
      console.error("Error deleting lesson type:", error);
      setError("Errore nell'eliminazione del tipo di lezione");
    } finally {
      setLoading(false);
    }
  };

  const getColorName = (color) => {
    const option = colorOptions.find((opt) => opt.value === color);
    return option ? option.label : color;
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">Gestione Tipi di Lezione</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
          disabled={loading}
        >
          Aggiungi Tipo
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Grid container spacing={2}>
        {lessonTypes.map((lessonType) => (
          <Grid item xs={12} md={6} lg={4} key={lessonType.id}>
            <Card
              sx={{
                border: `2px solid ${lessonType.color}`,
                borderRadius: 2,
              }}
            >
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Typography variant="h6">
                    {lessonType.name}
                  </Typography>
                  {lessonType.isOnline && (
                    <Chip
                      label="Online"
                      size="small"
                      color="info"
                    />
                  )}
                </Box>

                <Typography variant="body2" color="textSecondary" paragraph>
                  {lessonType.description}
                </Typography>

                <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                  <Chip
                    icon={<AccessTime />}
                    label={`${lessonType.duration} min`}
                    size="small"
                    variant="outlined"
                  />

                  {lessonType.price > 0 && (
                    <Chip
                      icon={<Euro />}
                      label={`€${lessonType.price}`}
                      size="small"
                      variant="outlined"
                    />
                  )}

                  <Chip
                    icon={<People />}
                    label={`Max ${lessonType.maxParticipants}`}
                    size="small"
                    variant="outlined"
                    color="primary"
                  />

                  <Chip
                    label={getColorName(lessonType.color)}
                    size="small"
                    sx={{
                      backgroundColor: lessonType.color,
                      color: "white",
                    }}
                  />
                </Box>
              </CardContent>

              <CardActions>
                <IconButton
                  size="small"
                  onClick={() => handleEdit(lessonType)}
                  disabled={loading}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleDelete(lessonType.id)}
                  disabled={loading}
                >
                  <DeleteIcon />
                </IconButton>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {lessonTypes.length === 0 && !loading && (
        <Paper elevation={1} sx={{ p: 4, textAlign: "center" }}>
          <Typography color="textSecondary">
            Nessun tipo di lezione configurato. Creane uno per iniziare.
          </Typography>
        </Paper>
      )}

      {/* Create/Edit Modal */}
      <Dialog
        open={modalOpen}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {modalEdit ? "Modifica Tipo di Lezione" : "Nuovo Tipo di Lezione"}
        </DialogTitle>

        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Nome della Lezione"
              value={modalLessonType.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              required
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Descrizione"
              multiline
              rows={3}
              value={modalLessonType.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              sx={{ mb: 2 }}
            />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Durata (minuti)"
                  type="number"
                  value={modalLessonType.duration}
                  onChange={(e) => handleInputChange("duration", parseInt(e.target.value) || 0)}
                  required
                  InputProps={{ inputProps: { min: 1, max: 480 } }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Prezzo (€)"
                  type="number"
                  value={modalLessonType.price}
                  onChange={(e) => handleInputChange("price", parseFloat(e.target.value) || 0)}
                  InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Max Partecipanti"
                  type="number"
                  value={modalLessonType.maxParticipants}
                  onChange={(e) => handleInputChange("maxParticipants", parseInt(e.target.value) || 0)}
                  required
                  InputProps={{ inputProps: { min: 0, max: 50 } }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Colore</InputLabel>
                  <Select
                    value={modalLessonType.color}
                    onChange={(e) => handleInputChange("color", e.target.value)}
                    label="Colore"
                  >
                    {colorOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              backgroundColor: option.value,
                              borderRadius: 1,
                            }}
                          />
                          {option.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={modalLessonType.isOnline}
                    onChange={(e) => handleInputChange("isOnline", e.target.checked)}
                  />
                }
                label="Lezione Online"
              />
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleClose}
            startIcon={<CancelIcon />}
            disabled={loading}
          >
            Annulla
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={loading}
          >
            {loading ? "Salvataggio..." : "Salva"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default LessonTypeManager;